import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, Shield, Zap } from "lucide-react";

const SaltSystems = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Salt Chlorine Generators" 
        description="Understanding, inspecting, and maintaining salt systems"
      >
        <section className="space-y-8">
          {/* How It Works */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">How Salt Systems Work</h2>
            <p className="text-muted-foreground mb-4">
              Salt chlorine generators (SCGs) convert dissolved salt into chlorine through electrolysis. 
              Water passes through a cell with titanium plates. Low-voltage electricity splits salt (NaCl) 
              into sodium and chlorine. The chlorine sanitizes the water, then recombines with sodium — 
              creating a continuous, self-renewing cycle.
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Salt → Electrolysis → Chlorine → Sanitization → Back to Salt</span>
            </div>
          </div>

          {/* Common Systems */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Common Systems</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Pentair IntelliChlor</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Ideal salt: 3000-3400 ppm</li>
                  <li>• Self-cleaning model available</li>
                  <li>• Integrates with automation</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Hayward AquaRite</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Ideal salt: 2700-3400 ppm</li>
                  <li>• Common in residential</li>
                  <li>• T-Cell design</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Jandy AquaPure</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Ideal salt: 3000-3500 ppm</li>
                  <li>• Flow sensor on cell</li>
                  <li>• iAquaLink compatible</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              <strong>Note:</strong> Always check manufacturer specs — salt ranges vary slightly between models.
            </p>
          </div>

          {/* Salt Levels */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Salt Level Guidelines</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="border p-3 text-left font-semibold">Level (ppm)</th>
                    <th className="border p-3 text-left font-semibold">Status</th>
                    <th className="border p-3 text-left font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="bg-destructive/10">
                    <td className="border p-3 font-medium">&lt;2500</td>
                    <td className="border p-3">Too Low</td>
                    <td className="border p-3">Add salt — cell may shut down</td>
                  </tr>
                  <tr>
                    <td className="border p-3 font-medium">2500-2900</td>
                    <td className="border p-3">Low</td>
                    <td className="border p-3">Add salt to reach ideal range</td>
                  </tr>
                  <tr className="bg-primary/10">
                    <td className="border p-3 font-medium text-primary">3000-3500</td>
                    <td className="border p-3 text-primary font-medium">Ideal</td>
                    <td className="border p-3">No action needed</td>
                  </tr>
                  <tr>
                    <td className="border p-3 font-medium">3500-4000</td>
                    <td className="border p-3">High</td>
                    <td className="border p-3">Monitor — may taste salty</td>
                  </tr>
                  <tr className="bg-amber-500/10">
                    <td className="border p-3 font-medium">&gt;4000</td>
                    <td className="border p-3">Too High</td>
                    <td className="border p-3">Partial drain/refill needed</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> Salt doesn't evaporate — only water does. 
                  Topping off a pool concentrates salt over time. The main way salt is lost is through 
                  splash-out, backwashing, or draining.
                </p>
              </div>
            </div>
          </div>

          {/* Cell Inspection */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Cell Inspection</h2>
            <p className="text-muted-foreground mb-4">
              Regular inspection catches scale buildup before it reduces chlorine production or damages the cell.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">What to Look For</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong className="text-foreground">White scale on plates:</strong> Calcium buildup — needs cleaning</li>
                <li>• <strong className="text-foreground">Flaking or pitting:</strong> End of cell life approaching</li>
                <li>• <strong className="text-foreground">Discoloration:</strong> May indicate improper chemistry or wear</li>
                <li>• <strong className="text-foreground">Visible damage:</strong> Cracks, broken plates = replacement needed</li>
              </ul>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Self-cleaning cells reverse polarity to shed scale, but still need occasional manual cleaning 
              in high-calcium areas like San Diego.
            </p>
          </div>

          {/* Cleaning Procedure */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Cell Cleaning Procedure</h2>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-destructive">Safety First</p>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• Wear safety goggles and acid-resistant gloves</li>
                    <li>• Work in well-ventilated area</li>
                    <li>• Always add acid to water, never water to acid</li>
                    <li>• Keep baking soda nearby to neutralize spills</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-4">Step-by-Step</h4>
              <ol className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <span><strong className="text-foreground">Turn off system</strong> — at the breaker, not just the controller</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <span><strong className="text-foreground">Remove cell</strong> — disconnect from plumbing (note flow direction arrow)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <span><strong className="text-foreground">Prepare solution</strong> — 4 parts water to 1 part muriatic acid in plastic container</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <span><strong className="text-foreground">Submerge cell</strong> — plates only, keep electrical connections dry</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <span><strong className="text-foreground">Wait</strong> — let fizz for 5-10 minutes (scale dissolving)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">6</span>
                  <span><strong className="text-foreground">Rinse thoroughly</strong> — garden hose, remove all acid residue</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">7</span>
                  <span><strong className="text-foreground">Inspect plates</strong> — should be clean and shiny</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">8</span>
                  <span><strong className="text-foreground">Reinstall</strong> — verify flow direction, secure connections</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">9</span>
                  <span><strong className="text-foreground">Neutralize & dispose</strong> — add baking soda to used acid, dispose properly</span>
                </li>
              </ol>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Caution:</strong> Don't soak longer than 15 minutes. 
                  Extended acid exposure can damage cell coating.
                </p>
              </div>
            </div>
          </div>

          {/* Troubleshooting */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Troubleshooting</h2>
            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">"Check Salt" or Low Salt Warning</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Test actual salt level — display may be inaccurate</li>
                  <li>• Add salt if truly low (allow 24 hours to dissolve)</li>
                  <li>• If salt is correct, cell or sensor may need cleaning/replacement</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Low Chlorine Production</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Check and clean cell for scale</li>
                  <li>• Verify adequate run time (cell only works when pump runs)</li>
                  <li>• Check output percentage setting</li>
                  <li>• Aging cell produces less — may need replacement</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">"No Flow" Error</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Verify pump is running</li>
                  <li>• Check for clogged baskets or dirty filter</li>
                  <li>• Clean flow sensor (may have scale buildup)</li>
                  <li>• Check water level — low level can trigger this</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Replacement Indicators */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">When to Replace</h2>
            <div className="bg-card border rounded-lg p-6">
              <ul className="space-y-2 text-muted-foreground">
                <li>• Scale won't clean off despite proper acid treatment</li>
                <li>• Chlorine production drops even with clean cell and correct settings</li>
                <li>• Visible plate damage, pitting, or flaking</li>
                <li>• Cell age: typically 3-7 years depending on use and water chemistry</li>
                <li>• Manufacturer error codes indicating cell failure</li>
              </ul>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> Note cell condition in the app. 
                  When you see early signs of wear, we can proactively discuss replacement with the client 
                  before it fails completely.
                </p>
              </div>
            </div>
          </div>

          {/* Documentation */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Documentation Requirements</h3>
            <p className="text-muted-foreground mb-4">
              For salt cell cleaning, log in the app:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Cell condition before cleaning (photo)</li>
              <li>• Cell condition after cleaning (photo)</li>
              <li>• Current salt reading</li>
              <li>• Output percentage setting</li>
              <li>• Any error codes or issues noted</li>
            </ul>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default SaltSystems;